import { css } from '@emotion/react'
import { Cell, Flow, Grid } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioGroupField, usePecField } from 'components/form'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'
import { CheckboxGroupField } from 'components/form/field/CheckboxGroupField'
import { InfoIcon } from 'components/InfoIcon'
import { RastreioCancerPerguntaOpcaoEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'

import { AnoSelectField } from '../../citopatologico/AnoSelectField'
import { SimNaoNaoSabeRadioField } from '../../citopatologico/SimNaoNaoSabeRadioField'
import { MamografiaPerguntaField, RISCO_CANCER_MAMA_TOOLTIP_TEXT } from '../model-mamografia'
import { MamografiaAnamneseCirurgiaTable } from './MamografiaAnamneseCirurgiaTable'
import { localizacaoRadioterapiaOptions, mamasExaminadasOptions, noduloCarocoOptions } from './model-anamnese'

export function MamografiaAnamneseModalStep() {
  const {
    input: { value: noduloCarocoValue },
  } = usePecField<RastreioCancerPerguntaOpcaoEnum[]>({
    name: MamografiaPerguntaField.noduloCaroco,
    subscription: { value: true },
  })

  const noduloIncluiNao =
    Array.isArray(noduloCarocoValue) && noduloCarocoValue.includes(RastreioCancerPerguntaOpcaoEnum.NAO)

  const noduloIncluiSim =
    Array.isArray(noduloCarocoValue) && noduloCarocoValue.some((value) => value !== RastreioCancerPerguntaOpcaoEnum.NAO)

  const noduloCarocoOptionsComputadas = noduloCarocoOptions.map((option) => ({
    ...option,
    disabled:
      (noduloIncluiNao && option.value !== RastreioCancerPerguntaOpcaoEnum.NAO) ||
      (noduloIncluiSim && option.value === RastreioCancerPerguntaOpcaoEnum.NAO),
  }))

  const {
    input: { value: fezMamografia },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(MamografiaPerguntaField.fezMamografia, {
    subscription: { value: true },
  })

  const isFezMamografiaSim = fezMamografia === RastreioCancerPerguntaOpcaoEnum.SIM

  const {
    input: { value: fezRadioterapia },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(MamografiaPerguntaField.fezRadioterapia, {
    subscription: { value: true },
  })

  const {
    input: { value: localizacao },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(MamografiaPerguntaField.radioterapiaLocalizacao, {
    subscription: { value: true },
  })

  const showRadioterapia = fezRadioterapia === RastreioCancerPerguntaOpcaoEnum.SIM
  const habilitaDireita =
    localizacao === RastreioCancerPerguntaOpcaoEnum.DIREITA || localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS
  const habilitaEsquerda =
    localizacao === RastreioCancerPerguntaOpcaoEnum.ESQUERDA || localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS

  return (
    <Grid>
      <Cell size={12} data-testid='noduloCaroco'>
        <CheckboxGroupField
          name={MamografiaPerguntaField.noduloCaroco}
          label='Tem nódulo ou caroço na mama?'
          options={noduloCarocoOptionsComputadas}
          required
        />
      </Cell>
      <Cell size={12} data-testid='riscoCancerMama'>
        <SimNaoNaoSabeRadioField
          label={
            <Flow direction='horizontal' gap={0.25}>
              <RequiredLabel inline fontWeight='bold' label='Apresenta risco elevado para câncer de mama?' />
              <InfoIcon icon='infoCircleFilled' text={RISCO_CANCER_MAMA_TOOLTIP_TEXT} tooltipStyle={styles.tooltip} />
            </Flow>
          }
          name={MamografiaPerguntaField.riscoCancerMama}
        />
      </Cell>
      <Cell size={12} data-testid='mamasExaminadas'>
        <RadioGroupField
          name={MamografiaPerguntaField.mamasExaminadas}
          label='Antes desta consulta, teve suas mamas examinadas por um profissional de saúde?'
          options={mamasExaminadasOptions}
          required
        />
      </Cell>
      <Cell size={12} data-testid='fezMamografia'>
        <SimNaoNaoSabeRadioField
          name={MamografiaPerguntaField.fezMamografia}
          label='Fez mamografia alguma vez?'
          required
        />
      </Cell>
      <Cell size={6} data-testid='anoUltimaMamografia'>
        <AnoSelectField
          name={MamografiaPerguntaField.anoUltimaMamografia}
          label='Quando fez a última mamografia?'
          placeholder='Ano da última mamografia'
          disabled={!isFezMamografiaSim}
          required={isFezMamografiaSim}
        />
      </Cell>
      <Cell size={12} data-testid='fezRadioterapia'>
        <SimNaoNaoSabeRadioField
          name={MamografiaPerguntaField.fezRadioterapia}
          label='Fez radioterapia na mama ou no plastão?'
          required
        />
      </Cell>
      {showRadioterapia && (
        <Cell size={12}>
          <Box>
            <Grid>
              <Cell size={12} data-testid='radioterapiaLocalizacao'>
                <RadioGroupField
                  name={MamografiaPerguntaField.radioterapiaLocalizacao}
                  label='Radioterapia - Localização'
                  options={localizacaoRadioterapiaOptions}
                  required
                />
              </Cell>
              <Cell size={6} data-testid='anoRadioterapiaMamaDireita'>
                <AnoSelectField
                  name={MamografiaPerguntaField.anoRadioterapiaMamaDireita}
                  label='Ano da radioterapia na mama direita'
                  disabled={!habilitaDireita}
                  required={habilitaDireita}
                />
              </Cell>
              <Cell size={6} data-testid='anoRadioterapiaMamaEsquerda'>
                <AnoSelectField
                  name={MamografiaPerguntaField.anoRadioterapiaMamaEsquerda}
                  label='Ano da radioterapia na mama esquerda'
                  disabled={!habilitaEsquerda}
                  required={habilitaEsquerda}
                />
              </Cell>
            </Grid>
          </Box>
        </Cell>
      )}
      <MamografiaAnamneseCirurgiaTable />
    </Grid>
  )
}

const styles = {
  tooltip: css`
    white-space: pre-wrap;
    max-width: 40rem;
  `,
}
